import { useState } from 'react';
import { FolderIcon, Plus, Pencil, Trash2 } from 'lucide-react';
import { Folder } from '@/types/task';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

const COLOR_OPTIONS = [
  '#6366f1', '#3b82f6', '#22c55e', '#f59e0b',
  '#ec4899', '#06b6d4', '#ef4444', '#a855f7',
];

interface FolderManagerProps {
  folders: Folder[];
  onAddFolder: (name: string, color: string) => Promise<Folder | null>;
  onUpdateFolder: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
  onDeleteFolder: (id: string) => Promise<void>;
  trigger?: React.ReactNode;
}

export function FolderManager({
  folders,
  onAddFolder,
  onUpdateFolder,
  onDeleteFolder,
  trigger,
}: FolderManagerProps) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(COLOR_OPTIONS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await onAddFolder(newName.trim(), newColor);
    setNewName('');
    setShowCreate(false);
  };

  const handleStartEdit = (folder: Folder) => {
    setEditingId(folder.id);
    setEditName(folder.name);
    setEditColor(folder.color);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editName.trim()) return;
    await onUpdateFolder(editingId, { name: editName.trim(), color: editColor });
    setEditingId(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await onDeleteFolder(deleteId);
    setDeleteId(null);
  };

  return (
    <>
      <Sheet>
        <SheetTrigger asChild>
          {trigger || (
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
              <FolderIcon className="h-4 w-4" />
              Folders
            </Button>
          )}
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Manage Folders</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {/* Folder list */}
            {folders.map((folder) => (
              <div key={folder.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                {editingId === folder.id ? (
                  <div className="flex-1 space-y-3">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="h-9"
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2">
                      {COLOR_OPTIONS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setEditColor(c)}
                          className={cn(
                            "w-6 h-6 rounded-full transition-all",
                            editColor === c && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                          )}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveEdit} disabled={!editName.trim()}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: folder.color }} />
                    <span className="flex-1 text-sm font-medium">{folder.name}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleStartEdit(folder)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteId(folder.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            ))}

            {/* Create new */}
            {showCreate ? (
              <div className="space-y-3 p-3 rounded-xl border border-dashed border-border">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Folder name..."
                  className="h-9"
                  autoFocus
                />
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewColor(c)}
                      className={cn(
                        "w-6 h-6 rounded-full transition-all",
                        newColor === c && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setNewName(''); }}>Cancel</Button>
                  <Button size="sm" onClick={handleCreate} disabled={!newName.trim()}>Create</Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full gap-2 border-dashed"
                onClick={() => setShowCreate(true)}
              >
                <Plus className="h-4 w-4" />
                New Folder
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder?</AlertDialogTitle>
            <AlertDialogDescription>
              Tasks in this folder will be kept but unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
